import React from 'react';
import * as R from 'ramda';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pure, compose, withState, lifecycle, withHandlers } from 'react-recompose';
// components
import { EmptyList } from '../../components/list';
import { FormButtons } from '../../components/form-buttons';
import DimmerComponent from '../../components/loader/dimmer';
import { makeSelectLoader } from '../../components/loader/selectors';
import { enhancePivotReport } from '../../components/edit-report/hocs';
import { openLoader, closeLoader } from '../../components/loader/actions';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { getColumnSettingsByReportType } from '../fleet-list/settings';
import { chargesColumnSettings } from '../payroll/settings/charges-table-settings';
import { columnSettings as fuelCardColumnSettings } from '../fuel-cards/settings/column-settings';
import { columnSettings as tollChargeColumnSettings } from '../toll-charges/settings/column-settings';
import { loadColumnSettings, orderColumnSettings } from '../dispatch-report/settings/column-settings';
import { columnSettings as payrollColumnSettings } from '../payroll/settings/payroll-list-table-settings';
import { columnSettings as routeByLoadColumnSettings } from '../dispatch-report/route/route-by-load/settings';
import { getColumnSettings as getCustomerInvoiceColumnSettings } from '../invoice/customer/settings/column-settings';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { apiDomain } from '../../helpers/env';
// ui
import { Box, Flex } from '../../ui';
// feature pivot-table
import { makeSelectUsedReport, makeSelectPivotTableData } from './selectors';
import {
  setUsedReport,
  updateReportRequest,
  createReportRequest,
  getPivotTableDataRequest,
} from './actions';
//////////////////////////////////////////////////

const licenseKeysMap = {
  localDevelopment: 'Z7CL-XJ055Q-5H2Z5G-0M0G5Z-2Y0P3P-0G1P6H-274E3Q-2A460B-1C3E57-73405U-3Q493Y-0A6T3D-07',
  development: 'Z71W-XGI14D-2B4H4M-4J6D1C-6J2M6T-282K0O-1M4S35-632P6X-3Z3734-4X323G',
  staging: 'Z755-XH6750-673M5D-2N1V0J-2A471U-4Y1J0Z-2T2T4L-57641Z-506Y2F-496D2L-3I26',
  production: 'Z7I3-XGFC1U-3Z3B2N-4U6R6S-5L4Z3G-0G1K3A-282Y4R-352O1U-1E4G08-4R0Z',
  production2: 'Z7DZ-XJI66K-2U1Z6I-383M05-1F3F58-5Q672B-5S5L6K-314A6D-4O4U5I-6Y6U1M-1U4R',
  northern_production: 'Z7ZR-XICG5U-552I0R-0K2I6W-2T334L-6F2J3C-182A56-48286U-1R082E-2T5P',
};

const getColumnSettings = ({ type }) => R.pathOr({}, [type], {
  [GC.PIVOT_TEL_REPORT]: loadColumnSettings,
  [GC.PIVOT_CLO_REPORT]: orderColumnSettings,
  [GC.PIVOT_FUEL_CARD_REPORT]: fuelCardColumnSettings,
  [GC.PIVOT_TOLL_CHARGE_REPORT]: tollChargeColumnSettings,
  [GC.PIVOT_DRIVER_PAYROLL_REPORT]: payrollColumnSettings,
  [GC.PIVOT_VENDOR_PAYROLL_REPORT]: payrollColumnSettings,
  [GC.PIVOT_PAYROLL_CHARGES_REPORT]: chargesColumnSettings,
  [GC.PIVOT_ROUTE_BY_LOAD_REPORT]: routeByLoadColumnSettings,
  [GC.PIVOT_VENDOR_PAYROLL_CHARGES_REPORT]: chargesColumnSettings,
  [GC.PIVOT_CUSTOMER_INVOICE_REPORT]: getCustomerInvoiceColumnSettings(),
  [GC.PIVOT_FLEET_EQUIPMENT_SERVICE_REPORT]: getColumnSettingsByReportType(GC.FLEET_EQUIPMENT_SERVICE_REPORT),
});

const getFieldName = (item, columnSettings, reference = false) => {
  let name = '';

  if (R.includes('charges.sumByAssessorial', item)) {
    name = `CHRG(Payroll): ${G.getAssessorialName(item)}`;
  } else if (R.includes('Assessorial', item)) {
    name = `CHRG: ${G.getAssessorialName(item)}`;
  } else if (reference) {
    name = G.getReferenceTitleName(item);
  } else {
    name = G.getWindowLocaleArr(R.pathOr(item, [item, GC.FIELD_NAME], columnSettings), item);
  }

  return R.trim(name);
};

const getPivotType = (name, columnSettings) => {
  if (R.includes('Assessorial', name)) return 'number';

  return R.pathOr('string', [name, 'pivotType'], columnSettings);
};

const mapDataForRocks = (data, report) => {
  if (G.isNilOrEmpty(data)) return [];

  const { type, fields } = report;

  const columnSettings = getColumnSettings({ type });

  const head = R.compose(
    R.indexBy(R.prop('item')),
    R.map(({ name, reference }) => ({
      item: name,
      type: getPivotType(name, columnSettings),
      caption: getFieldName(name, columnSettings, reference),
    })),
  )(fields);

  return R.prepend(head, data);
};

const getRocksReport = (props, withCustomizedContextMenu) => {
  const { recordIdName, selectedReport } = props;

  const data = R.path(['pivotTableData'], props);
  const pivotSettings = R.path(['pivotSettings'], selectedReport);
  const dataToUse = mapDataForRocks(R.path(['results'], data), selectedReport);

  const dataSource = {
    data: dataToUse,
    dataSourceType: 'json',
    mapping: withCustomizedContextMenu
      ? {
        ...R.head(dataToUse),
        [R.or(recordIdName, GC.FIELD_GUID)]: {
          [GC.FIELD_TYPE]: GC.FIELD_ID,
        },
      }
      : null,
  };

  if (G.isNotNilAndNotEmpty(pivotSettings)) {
    return {
      ...JSON.parse(pivotSettings),
      dataSource,
    };
  }

  return { dataSource };
};

const formatterMap = {
  enum: G.getEnumLocale,
  state: G.getStateValueByConfig,
};

const enhancePivotUI = compose(
  lifecycle({
    componentDidMount() {
      const data = R.path(['pivotTableData'], this.props);

      if (G.isNilOrEmpty(data)) return;

      const { selectedReport } = this.props;

      const { type } = selectedReport;

      const columnSettings = getColumnSettings({ type });

      setTimeout(
        () => {
          const { additionalCellActions } = this.props;

          const withCustomizedContextMenu = G.isNotNilAndNotEmpty(additionalCellActions);

          let mapKey = apiDomain;

          if (R.includes('amoustms.app', window.location.host)) {
            mapKey = 'production2';
          }

          const pivot = new Flexmonster({ // eslint-disable-line
            height: '97%',
            toolbar: true,
            container: 'pivot-container',
            licenseKey: licenseKeysMap[mapKey],
            componentFolder: 'https://cdn.flexmonster.com/',
            report: getRocksReport(this.props, withCustomizedContextMenu),
            customizeCell: (cell, data) => {
              if (R.equals(data.type === 'header') && G.isNotNil(data.hierarchy)) {
                const { type } = R.pathOr({}, [data.hierarchy.uniqueName], columnSettings);

                const formatter = R.prop(type, formatterMap);

                if (G.isFunction(formatter)) {
                  cell.text = formatter(data.label); // eslint-disable-line
                }
              }
            },
          });

          if (withCustomizedContextMenu) {
            pivot.customizeContextMenu((items, data, viewType) => {
              if (R.and(G.isObject(data), R.equals(viewType, 'pivot'))) {
                const { member, measure, recordId } = data;

                if (R.or(G.isAllNilOrEmpty([member, measure]), G.notEquals(R.length(R.uniq(recordId)), 1))) {
                  return items;
                }

                const cellActions = R.map(
                  ({ label, action }) => ({
                    label,
                    handler: () => {
                      action(data);

                      const { toolbar } = pivot;

                      if (
                        R.and(G.isFunction(toolbar.isFullscreen), G.isFunction(toolbar.exitFullscreen)) &&
                        G.isTrue(toolbar.isFullscreen())
                      ) toolbar.exitFullscreen();
                    },
                  }),
                  additionalCellActions,
                );

                return R.concat(items, cellActions);
              }

              return items;
            });
          }

          window.flexMonsterPivotInstance = pivot;
        },
        500,
      );
    },
  }),
  pure,
);

const FlexMonsterUI = enhancePivotUI(() => null);

const enhance = compose(
  withState('pivotState', 'setPivotState', {}),
  enhancePivotReport,
  withHandlers({
    handleChangePivotState: (props) => (s) => {
      const { openLoader, closeLoader, setPivotState } = props;

      openLoader();
      setTimeout(() => setPivotState(s, closeLoader), 10);
    },
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      if (G.checkPropNotEqualInObjects('selectedReport', this.props, prevProps)) {
        const { selectedReport } = this.props;

        if (G.isNilOrEmpty(selectedReport)) return;

        const rows = R.pathOr([], ['rows'], selectedReport);
        const cols = R.pathOr([], ['columns'], selectedReport);
        const vals = R.pathOr([], ['aggregatedValues'], selectedReport);
        const rendererName = R.pathOr('Table', ['renderType'], selectedReport);
        const aggregatorName = R.pathOr('Count', ['aggregation'], selectedReport);
        const columnSettings = getColumnSettings(selectedReport);
        const pivotState = {
          vals,
          rendererName,
          aggregatorName,
          cols: cols.map((item) => (
            R.trim(G.getWindowLocaleArr(R.pathOr(item, [item, 'name'], columnSettings), item),
          ))),
          rows: rows.map((item) => (
            R.trim(G.getWindowLocaleArr(R.pathOr(item, [item, 'name'], columnSettings), item),
          ))),
        };
        this.props.setPivotState(pivotState);
      }
    },
  }),
  pure,
);

export const PivotTable = (props) => {
  const {
    loader,
    userLoginId,
    footerPosition,
    pivotTableData,
    selectedReport,
    pivotTableMaxHeight,
    handleClickSavePivot,
    handleClickSaveAsNewPivot,
  } = props;

  if (G.isNilOrEmpty(R.path(['results'], pivotTableData))) {
    return (
      <Box>
        {loader.showDimmer && <DimmerComponent tableCount={3} rectangleCount={4} />}
        <EmptyList>{G.getWindowLocale('titles:emptyList', 'Empty List')}</EmptyList>
      </Box>
    );
  }

  const saveDisabled = R.or(
    R.includes('Default', G.getGuidFromObject(selectedReport)),
    R.not(R.propEq(userLoginId, GC.FIELD_OWNER, selectedReport)),
  );

  return (
    <Box>
      {loader.showDimmer && <DimmerComponent tableCount={3} rectangleCount={4} />}
      <Flex
        m={10}
        overflow='scroll'
        flexDirection='column'
        alignItems='flex-start'
        height='calc(100vh - 125px)'
        maxHeight={pivotTableMaxHeight}
      >
        <div id='pivot-container' />
        <FlexMonsterUI {...props} />
      </Flex>
      <FormButtons
        showCancel={false}
        showSaveAsNew={true}
        position={footerPosition}
        saveDisabled={saveDisabled}
        handleClickSave={handleClickSavePivot}
        handleClickSaveAsNew={handleClickSaveAsNewPivot}
      />
    </Box>
  );
};

const mapStateToProps = (state) => createStructuredSelector({
  loader: makeSelectLoader(state),
  selectedReport: makeSelectUsedReport(state),
  branchGuid: makeSelectCurrentBranchGuid(state),
  pivotTableData: makeSelectPivotTableData(state),
});

export default connect(mapStateToProps, {
  openLoader,
  closeLoader,
  setUsedReport,
  createReportRequest,
  updateReportRequest,
  getPivotTableDataRequest,
})(enhance(PivotTable));
